import { useState } from 'react';
import { 
  FaRobot, 
  FaChartLine, 
  FaRoute, 
  FaSearch, 
  FaStar, 
  FaRegStar,
  FaRegClock,
  FaGraduationCap,
  FaChevronRight,
  FaFilter,
  FaBookmark,
  FaRegBookmark,
  FaUserGraduate,
  FaCode,
  FaPalette,
  FaChartBar,
  FaLaptopCode,
  FaMobileAlt,
  FaServer,
  FaNetworkWired
} from 'react-icons/fa';

const SmartContentDiscovery = () => {
  const [activeTab, setActiveTab] = useState('recommendations');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    difficulty: [],
    duration: [],
    rating: null,
    categories: []
  });
  
  // Sample data for demonstration
  const recommendedCourses = [
    { 
      id: 1, 
      title: 'Advanced React Patterns', 
      instructor: 'Alex Johnson',
      rating: 4.8,
      reviews: 342,
      level: 'Advanced',
      duration: '8 weeks',
      category: 'Web Development',
      image: null,
      match: 98,
      reason: 'Based on your completion of React Fundamentals',
      bookmarked: true
    },
    { 
      id: 2, 
      title: 'Machine Learning Fundamentals', 
      instructor: 'Maria Garcia',
      rating: 4.9,
      reviews: 567,
      level: 'Intermediate',
      duration: '10 weeks',
      category: 'Data Science',
      image: null,
      match: 95,
      reason: 'Aligns with your career goals in AI',
      bookmarked: false
    },
    { 
      id: 3, 
      title: 'Full-Stack JavaScript Development', 
      instructor: 'David Kim',
      rating: 4.7,
      reviews: 289,
      level: 'Intermediate',
      duration: '12 weeks',
      category: 'Web Development',
      image: null,
      match: 92,
      reason: 'Complements your Node.js skills',
      bookmarked: false
    },
    { 
      id: 4, 
      title: 'UI/UX Design Principles', 
      instructor: 'Sarah Williams',
      rating: 4.6,
      reviews: 218,
      level: 'Beginner',
      duration: '6 weeks',
      category: 'Design',
      image: null,
      match: 88,
      reason: 'Based on your interest in frontend development',
      bookmarked: true
    },
  ];
  
  const trendingCourses = [
    { 
      id: 5, 
      title: 'Blockchain Development with Solidity', 
      instructor: 'James Wilson',
      rating: 4.7,
      reviews: 156,
      level: 'Intermediate',
      duration: '8 weeks',
      category: 'Blockchain',
      image: null,
      students: 2845,
      growth: 128, // percentage growth in enrollment
      bookmarked: false
    },
    { 
      id: 6, 
      title: 'Data Visualization with D3.js', 
      instructor: 'Emma Davis',
      rating: 4.8,
      reviews: 203,
      level: 'Intermediate',
      duration: '6 weeks',
      category: 'Data Science',
      image: null,
      students: 1932,
      growth: 85,
      bookmarked: false
    },
    { 
      id: 7, 
      title: 'DevOps Engineering & CI/CD', 
      instructor: 'Noah Thompson',
      rating: 4.9,
      reviews: 312,
      level: 'Advanced',
      duration: '10 weeks',
      category: 'DevOps',
      image: null,
      students: 3156,
      growth: 75,
      bookmarked: false
    },
    { 
      id: 8, 
      title: 'Mobile App Development with Flutter', 
      instructor: 'Olivia Martinez',
      rating: 4.6,
      reviews: 178,
      level: 'Beginner',
      duration: '8 weeks',
      category: 'Mobile Development',
      image: null,
      students: 2567,
      growth: 62,
      bookmarked: false
    },
  ];
  
  const learningPaths = [
    {
      id: 1,
      title: 'Full-Stack Web Developer',
      description: 'Master both frontend and backend development to build complete web applications',
      courses: 5,
      duration: '6 months',
      level: 'Beginner to Advanced',
      skills: ['HTML/CSS', 'JavaScript', 'React', 'Node.js', 'MongoDB', 'Express'],
      progress: 35,
      enrolled: true,
      icon: <FaLaptopCode className="text-blue-500" />
    },
    {
      id: 2,
      title: 'Data Scientist',
      description: 'Learn to analyze and interpret complex data to make data-driven decisions',
      courses: 6,
      duration: '8 months',
      level: 'Intermediate to Advanced',
      skills: ['Python', 'Statistics', 'Machine Learning', 'Data Visualization', 'SQL', 'Big Data'],
      progress: 0,
      enrolled: false,
      icon: <FaChartBar className="text-purple-500" />
    },
    {
      id: 3,
      title: 'Mobile App Developer',
      description: 'Build native and cross-platform mobile applications for iOS and Android',
      courses: 4,
      duration: '5 months',
      level: 'Beginner to Intermediate',
      skills: ['React Native', 'Flutter', 'iOS Development', 'Android Development', 'UI/UX for Mobile'],
      progress: 0,
      enrolled: false,
      icon: <FaMobileAlt className="text-green-500" />
    },
    {
      id: 4,
      title: 'DevOps Engineer',
      description: 'Master the tools and practices for continuous integration and deployment',
      courses: 5,
      duration: '7 months',
      level: 'Intermediate to Advanced',
      skills: ['Linux', 'Docker', 'Kubernetes', 'CI/CD', 'Cloud Services', 'Monitoring'],
      progress: 0,
      enrolled: false,
      icon: <FaServer className="text-red-500" />
    },
  ];
  
  const searchResults = [
    ...recommendedCourses,
    ...trendingCourses,
    { 
      id: 9, 
      title: 'Python for Data Science', 
      instructor: 'Benjamin Clark',
      rating: 4.8,
      reviews: 423,
      level: 'Beginner',
      duration: '8 weeks',
      category: 'Data Science',
      image: null,
      bookmarked: false
    },
    { 
      id: 10, 
      title: 'Network Security Fundamentals', 
      instructor: 'Sophia Lee',
      rating: 4.7,
      reviews: 198,
      level: 'Intermediate',
      duration: '6 weeks',
      category: 'Cybersecurity',
      image: null,
      bookmarked: false
    },
    { 
      id: 11, 
      title: 'Cloud Architecture with AWS', 
      instructor: 'Ethan Wright',
      rating: 4.9,
      reviews: 312,
      level: 'Advanced',
      duration: '10 weeks',
      category: 'Cloud Computing',
      image: null,
      bookmarked: false
    },
    { 
      id: 12, 
      title: 'Responsive Web Design', 
      instructor: 'Isabella Rodriguez',
      rating: 4.6,
      reviews: 267,
      level: 'Beginner',
      duration: '4 weeks',
      category: 'Web Development',
      image: null,
      bookmarked: false
    },
  ];
  
  // Filter options
  const filterOptions = {
    difficulty: ['Beginner', 'Intermediate', 'Advanced'],
    duration: ['< 4 weeks', '4-8 weeks', '8-12 weeks', '> 12 weeks'],
    rating: [3, 3.5, 4, 4.5],
    categories: ['Web Development', 'Data Science', 'Mobile Development', 'DevOps', 'Design', 'Blockchain', 'Cloud Computing', 'Cybersecurity']
  };
  
  // Toggle filter selection
  const toggleFilter = (type, value) => {
    setSelectedFilters(prev => {
      if (type === 'rating') {
        return { ...prev, [type]: prev[type] === value ? null : value };
      }
      
      const updatedFilters = { ...prev };
      if (updatedFilters[type].includes(value)) {
        updatedFilters[type] = updatedFilters[type].filter(item => item !== value);
      } else {
        updatedFilters[type] = [...updatedFilters[type], value];
      }
      return updatedFilters;
    });
  };
  
  // Filter courses based on selected filters and search query
  const filterCourses = (courses) => {
    if (searchQuery === '' && Object.values(selectedFilters).every(filter => 
      Array.isArray(filter) ? filter.length === 0 : filter === null
    )) {
      return courses;
    }
    
    return courses.filter(course => {
      // Search query filter
      const matchesSearch = searchQuery === '' || 
        course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.instructor.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.category.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Difficulty filter
      const matchesDifficulty = selectedFilters.difficulty.length === 0 || 
        selectedFilters.difficulty.includes(course.level);
      
      // Duration filter
      const matchesDuration = selectedFilters.duration.length === 0 || 
        selectedFilters.duration.some(duration => {
          const weeks = parseInt(course.duration);
          if (duration === '< 4 weeks') return weeks < 4;
          if (duration === '4-8 weeks') return weeks >= 4 && weeks <= 8;
          if (duration === '8-12 weeks') return weeks > 8 && weeks <= 12;
          if (duration === '> 12 weeks') return weeks > 12;
          return false;
        });
      
      // Rating filter
      const matchesRating = selectedFilters.rating === null || 
        course.rating >= selectedFilters.rating;
      
      // Category filter
      const matchesCategory = selectedFilters.categories.length === 0 || 
        selectedFilters.categories.includes(course.category);
      
      return matchesSearch && matchesDifficulty && matchesDuration && matchesRating && matchesCategory;
    });
  };
  
  // Get filtered courses based on active tab
  const getFilteredCourses = () => {
    if (activeTab === 'search') {
      return filterCourses(searchResults);
    }
    if (activeTab === 'recommendations') {
      return filterCourses(recommendedCourses);
    }
    if (activeTab === 'trending') {
      return filterCourses(trendingCourses);
    }
    return [];
  };
  
  const filteredCourses = getFilteredCourses();
  
  // Toggle bookmark
  const toggleBookmark = (courseId) => {
    // In a real app, this would update the state and make an API call
    console.log(`Toggling bookmark for course ${courseId}`);
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Web Development':
        return <FaCode className="text-blue-500" />;
      case 'Data Science':
        return <FaChartBar className="text-purple-500" />;
      case 'Mobile Development':
        return <FaMobileAlt className="text-green-500" />;
      case 'DevOps':
        return <FaServer className="text-red-500" />;
      case 'Design':
        return <FaPalette className="text-pink-500" />;
      case 'Blockchain':
        return <FaNetworkWired className="text-yellow-500" />;
      case 'Cloud Computing':
        return <FaServer className="text-blue-400" />;
      case 'Cybersecurity':
        return <FaLock className="text-red-600" />;
      default:
        return <FaGraduationCap className="text-gray-500" />;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 px-6 py-4 border-b">
        <h2 className="text-lg font-medium">Smart Content Discovery</h2>
        <p className="text-sm text-gray-500 mt-1">Find the perfect courses for your learning journey</p>
      </div>

      {/* Search Bar */}
      <div className="p-6 border-b">
        <div className="relative">
          <input
            type="text"
            placeholder="Search for courses, topics, or instructors..."
            className="w-full bg-gray-100 border border-gray-300 rounded-md py-3 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (e.target.value && activeTab !== 'search') {
                setActiveTab('search');
              }
            }}
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="h-5 w-5 text-gray-400" />
          </div>
          <button 
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter className={`h-5 w-5 ${showFilters ? 'text-primary' : 'text-gray-400'}`} />
          </button>
        </div>
        
        {/* Filters */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex flex-wrap -mx-2">
              <div className="px-2 w-full md:w-1/4 mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Difficulty</h4>
                <div className="space-y-2">
                  {filterOptions.difficulty.map((level) => (
                    <label key={level} className="flex items-center">
                      <input 
                        type="checkbox" 
                        className="form-checkbox h-4 w-4 text-primary rounded focus:ring-primary" 
                        checked={selectedFilters.difficulty.includes(level)}
                        onChange={() => toggleFilter('difficulty', level)}
                      />
                      <span className="ml-2 text-sm text-gray-700">{level}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="px-2 w-full md:w-1/4 mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Duration</h4>
                <div className="space-y-2">
                  {filterOptions.duration.map((duration) => (
                    <label key={duration} className="flex items-center">
                      <input 
                        type="checkbox" 
                        className="form-checkbox h-4 w-4 text-primary rounded focus:ring-primary" 
                        checked={selectedFilters.duration.includes(duration)}
                        onChange={() => toggleFilter('duration', duration)}
                      />
                      <span className="ml-2 text-sm text-gray-700">{duration}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="px-2 w-full md:w-1/4 mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Minimum Rating</h4>
                <div className="space-y-2">
                  {filterOptions.rating.map((rating) => (
                    <label key={rating} className="flex items-center">
                      <input 
                        type="radio" 
                        name="rating" 
                        className="form-radio h-4 w-4 text-primary focus:ring-primary" 
                        checked={selectedFilters.rating === rating}
                        onChange={() => toggleFilter('rating', rating)}
                      />
                      <span className="ml-2 text-sm text-gray-700 flex items-center">
                        {rating}+ <FaStar className="text-yellow-400 ml-1" />
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="px-2 w-full md:w-1/4 mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Categories</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {filterOptions.categories.map((category) => (
                    <label key={category} className="flex items-center">
                      <input 
                        type="checkbox" 
                        className="form-checkbox h-4 w-4 text-primary rounded focus:ring-primary" 
                        checked={selectedFilters.categories.includes(category)}
                        onChange={() => toggleFilter('categories', category)}
                      />
                      <span className="ml-2 text-sm text-gray-700">{category}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end mt-4">
              <button 
                className="text-sm text-gray-600 hover:text-gray-800 mr-4"
                onClick={() => setSelectedFilters({
                  difficulty: [],
                  duration: [],
                  rating: null,
                  categories: []
                })}
              >
                Clear All
              </button>
              <button 
                className="bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-dark transition-colors"
                onClick={() => setShowFilters(false)}
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex -mb-px">
          <button
            onClick={() => setActiveTab('recommendations')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'recommendations' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaRobot className="inline mr-2" />
            AI Recommendations
          </button>
          <button
            onClick={() => setActiveTab('trending')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'trending' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaChartLine className="inline mr-2" />
            Trending Courses
          </button>
          <button
            onClick={() => setActiveTab('paths')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'paths' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaRoute className="inline mr-2" />
            Learning Paths
          </button>
          {searchQuery && (
            <button
              onClick={() => setActiveTab('search')}
              className={`py-4 px-6 text-sm font-medium ${activeTab === 'search' 
                ? 'border-b-2 border-primary text-primary' 
                : 'text-gray-500 hover:text-gray-700'}`}
            >
              <FaSearch className="inline mr-2" />
              Search Results
            </button>
          )}
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* AI Recommendations */}
        {activeTab === 'recommendations' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Personalized Course Recommendations</h3>
              <span className="text-sm text-gray-500">
                Based on your learning history and goals
              </span>
            </div>
            
            {filteredCourses.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {filteredCourses.map(course => (
                  <div key={course.id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                    <div className="relative">
                      {course.image ? (
                        <img src={course.image} alt={course.title} className="w-full h-40 object-cover" />
                      ) : (
                        <div className="w-full h-40 bg-gray-200 flex items-center justify-center">
                          {getCategoryIcon(course.category)}
                          <span className="sr-only">{course.category}</span>
                        </div>
                      )}
                      <div className="absolute top-2 right-2">
                        <button 
                          className="p-1.5 rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 transition-colors"
                          onClick={() => toggleBookmark(course.id)}
                        >
                          {course.bookmarked ? (
                            <FaBookmark className="text-primary" />
                          ) : (
                            <FaRegBookmark className="text-gray-600" />
                          )}
                        </button>
                      </div>
                      {course.match && (
                        <div className="absolute top-2 left-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                          {course.match}% Match
                        </div>
                      )}
                    </div>
                    
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900 mb-1 line-clamp-2">{course.title}</h4>
                      <p className="text-sm text-gray-600 mb-2">{course.instructor}</p>
                      
                      <div className="flex items-center mb-2">
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <span key={i}>
                              {i < Math.floor(course.rating) ? (
                                <FaStar className="text-yellow-400 w-4 h-4" />
                              ) : (
                                <FaRegStar className="text-yellow-400 w-4 h-4" />
                              )}
                            </span>
                          ))}
                        </div>
                        <span className="text-sm text-gray-600 ml-1">{course.rating}</span>
                        <span className="text-xs text-gray-500 ml-1">({course.reviews})</span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>{course.level}</span>
                        <span>•</span>
                        <span>{course.duration}</span>
                        <span>•</span>
                        <span>{course.category}</span>
                      </div>
                      
                      {course.reason && (
                        <div className="mt-3 text-xs text-gray-600 bg-blue-50 p-2 rounded-md">
                          <span className="font-medium">Why we recommend this:</span> {course.reason}
                        </div>
                      )}
                      
                      <button className="w-full mt-4 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-dark transition-colors">
                        View Course
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">No courses match your current filters. Try adjusting your search criteria.</p>
              </div>
            )}
          </div>
        )}

        {/* Trending Courses */}
        {activeTab === 'trending' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Popular & Trending Courses</h3>
              <span className="text-sm text-gray-500">
                Courses with high enrollment growth
              </span>
            </div>
            
            {filteredCourses.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {filteredCourses.map(course => (
                  <div key={course.id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                    <div className="relative">
                      {course.image ? (
                        <img src={course.image} alt={course.title} className="w-full h-40 object-cover" />
                      ) : (
                        <div className="w-full h-40 bg-gray-200 flex items-center justify-center">
                          {getCategoryIcon(course.category)}
                          <span className="sr-only">{course.category}</span>
                        </div>
                      )}
                      <div className="absolute top-2 right-2">
                        <button 
                          className="p-1.5 rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 transition-colors"
                          onClick={() => toggleBookmark(course.id)}
                        >
                          {course.bookmarked ? (
                            <FaBookmark className="text-primary" />
                          ) : (
                            <FaRegBookmark className="text-gray-600" />
                          )}
                        </button>
                      </div>
                      {course.growth && (
                        <div className="absolute top-2 left-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                          +{course.growth}% Growth
                        </div>
                      )}
                    </div>
                    
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900 mb-1 line-clamp-2">{course.title}</h4>
                      <p className="text-sm text-gray-600 mb-2">{course.instructor}</p>
                      
                      <div className="flex items-center mb-2">
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <span key={i}>
                              {i < Math.floor(course.rating) ? (
                                <FaStar className="text-yellow-400 w-4 h-4" />
                              ) : (
                                <FaRegStar className="text-yellow-400 w-4 h-4" />
                              )}
                            </span>
                          ))}
                        </div>
                        <span className="text-sm text-gray-600 ml-1">{course.rating}</span>
                        <span className="text-xs text-gray-500 ml-1">({course.reviews})</span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>{course.level}</span>
                        <span>•</span>
                        <span>{course.duration}</span>
                        <span>•</span>
                        <span>{course.category}</span>
                      </div>
                      
                      {course.students && (
                        <div className="mt-3 text-xs text-gray-600 bg-orange-50 p-2 rounded-md flex items-center">
                          <FaUserGraduate className="text-orange-500 mr-1" />
                          <span>{course.students.toLocaleString()} students enrolled</span>
                        </div>
                      )}
                      
                      <button className="w-full mt-4 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-dark transition-colors">
                        View Course
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">No courses match your current filters. Try adjusting your search criteria.</p>
              </div>
            )}
          </div>
        )}

        {/* Learning Paths */}
        {activeTab === 'paths' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Curated Learning Paths</h3>
              <span className="text-sm text-gray-500">
                Structured course sequences for specific career goals
              </span>
            </div>
            
            <div className="space-y-6">
              {learningPaths.map(path => (
                <div key={path.id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                  <div className="p-6">
                    <div className="flex items-start">
                      <div className="p-3 rounded-full bg-gray-100 mr-4">
                        {path.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-lg font-medium text-gray-900">{path.title}</h4>
                            <p className="text-sm text-gray-600 mt-1">{path.description}</p>
                          </div>
                          <button 
                            className={`${path.enrolled 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-primary text-white hover:bg-primary-dark'} rounded-md px-4 py-2 text-sm font-medium transition-colors`}
                          >
                            {path.enrolled ? 'Continue Learning' : 'Enroll Now'}
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center">
                              <FaGraduationCap className="text-gray-400 mr-2" />
                              <div>
                                <h5 className="text-sm font-medium text-gray-700">Courses</h5>
                                <p className="text-lg font-bold text-gray-900">{path.courses}</p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center">
                              <FaRegClock className="text-gray-400 mr-2" />
                              <div>
                                <h5 className="text-sm font-medium text-gray-700">Duration</h5>
                                <p className="text-lg font-bold text-gray-900">{path.duration}</p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center">
                              <FaChartLine className="text-gray-400 mr-2" />
                              <div>
                                <h5 className="text-sm font-medium text-gray-700">Level</h5>
                                <p className="text-lg font-bold text-gray-900">{path.level}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-4">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Skills You'll Learn</h5>
                          <div className="flex flex-wrap">
                            {path.skills.map((skill, index) => (
                              <span key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-2 mb-2">{skill}</span>
                            ))}
                          </div>
                        </div>
                        
                        {path.enrolled && (
                          <div className="mt-4">
                            <div className="flex justify-between text-sm text-gray-600 mb-1">
                              <span>Progress</span>
                              <span>{path.progress}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                              <div 
                                className="bg-green-500 h-2.5 rounded-full" 
                                style={{ width: `${path.progress}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-6 flex justify-end">
                      <button className="text-primary hover:text-primary-dark text-sm font-medium flex items-center">
                        View Path Details <FaChevronRight className="ml-1" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search Results */}
        {activeTab === 'search' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Search Results</h3>
              <span className="text-sm text-gray-500">
                {filteredCourses.length} courses found
              </span>
            </div>
            
            {filteredCourses.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {filteredCourses.map(course => (
                  <div key={course.id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                    <div className="relative">
                      {course.image ? (
                        <img src={course.image} alt={course.title} className="w-full h-40 object-cover" />
                      ) : (
                        <div className="w-full h-40 bg-gray-200 flex items-center justify-center">
                          {getCategoryIcon(course.category)}
                          <span className="sr-only">{course.category}</span>
                        </div>
                      )}
                      <div className="absolute top-2 right-2">
                        <button 
                          className="p-1.5 rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 transition-colors"
                          onClick={() => toggleBookmark(course.id)}
                        >
                          {course.bookmarked ? (
                            <FaBookmark className="text-primary" />
                          ) : (
                            <FaRegBookmark className="text-gray-600" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900 mb-1 line-clamp-2">{course.title}</h4>
                      <p className="text-sm text-gray-600 mb-2">{course.instructor}</p>
                      
                      <div className="flex items-center mb-2">
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <span key={i}>
                              {i < Math.floor(course.rating) ? (
                                <FaStar className="text-yellow-400 w-4 h-4" />
                              ) : (
                                <FaRegStar className="text-yellow-400 w-4 h-4" />
                              )}
                            </span>
                          ))}
                        </div>
                        <span className="text-sm text-gray-600 ml-1">{course.rating}</span>
                        <span className="text-xs text-gray-500 ml-1">({course.reviews})</span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>{course.level}</span>
                        <span>•</span>
                        <span>{course.duration}</span>
                        <span>•</span>
                        <span>{course.category}</span>
                      </div>
                      
                      <button className="w-full mt-4 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-dark transition-colors">
                        View Course
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">No courses match your search criteria. Try different keywords or filters.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Missing FaLock icon
const FaLock = ({ className }) => (
  <svg 
    className={className} 
    fill="currentColor" 
    viewBox="0 0 448 512" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M400 224h-24v-72C376 68.2 307.8 0 224 0S72 68.2 72 152v72H48c-26.5 0-48 21.5-48 48v192c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V272c0-26.5-21.5-48-48-48zm-104 0H152v-72c0-39.7 32.3-72 72-72s72 32.3 72 72v72z"></path>
  </svg>
);

export default SmartContentDiscovery;